/**
 * Name and share a saved dashboard view - see dashboard-views.ts.
 *
 * One small form: the view's name, who can open it (only me, chosen
 * colleagues, or everyone in the organisation), and - for chosen colleagues -
 * a searchable picker of the organisation's members.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useForm } from '@/shared/lib/forms'
import type { FormField, SelectOptionShape } from '@/shared/lib/forms'
import type { TableViewShareScope } from '@/admin/types/table-views'

const FORM_ID = 'dashboard-view-form'

export interface DashboardViewDraft {
  name: string
  scope: TableViewShareScope
  people: string[]
}

interface DialogProps {
  open: boolean
  mode: 'create' | 'edit'
  initial: DashboardViewDraft
  /** The organisation's name, for "Everyone at <org>". */
  orgName: string
  /** Colleagues the view can be shared with (value = user uuid). */
  colleagues: SelectOptionShape[]
  isPending: boolean
  onClose: () => void
  onSubmit: (draft: DashboardViewDraft) => Promise<unknown>
}

export function DashboardViewDialog({ open, ...rest }: DialogProps) {
  // Mount the form only while open so it seeds from the current view each time.
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : rest.onClose())}>
      <DialogContent className="sm:max-w-md">{open && <ViewForm {...rest} />}</DialogContent>
    </Dialog>
  )
}

function ViewForm({
  mode,
  initial,
  orgName,
  colleagues,
  isPending,
  onClose,
  onSubmit,
}: Omit<DialogProps, 'open'>) {
  const { t } = useTranslation()
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'name',
        label: t('dashboard.views.nameLabel', 'View name'),
        placeholder: t('dashboard.views.namePlaceholder', 'e.g. Monday claims review'),
        value: initial.name,
        maxLength: 100,
        required: t('dashboard.views.nameRequired', 'Give the view a name'),
      },
      {
        type: 'select',
        name: 'scope',
        label: t('dashboard.views.scopeLabel', 'Who can open it'),
        value: initial.scope,
        options: [
          { value: 'private', label: t('dashboard.views.scopePrivate', 'Only me') },
          { value: 'people', label: t('dashboard.views.scopePeople', 'Colleagues I choose') },
          {
            value: 'organisation',
            label: t('dashboard.views.scopeOrganisation', {
              defaultValue: 'Everyone at {{org}}',
              org: orgName,
            }),
          },
        ],
        hint: t(
          'dashboard.views.scopeHint',
          'People you share with can open the view. Only you can change it.',
        ),
      },
      {
        type: 'multiSelect',
        name: 'people',
        label: t('dashboard.views.peopleLabel', 'Colleagues'),
        placeholder: t('dashboard.views.peoplePlaceholder', 'Search by name or email'),
        value: initial.people,
        options: colleagues,
        visible: (ctx) => ctx.values.scope === 'people',
      },
    ],
    [t, initial, orgName, colleagues],
  )

  const { Form } = useForm(
    { name: FORM_ID, submit: 'footer', fields },
    async (values, formCtx) => {
      const v = values as { name: string; scope: TableViewShareScope; people?: string[] }
      // Cross-field rule: "Colleagues I choose" needs at least one colleague.
      // The server enforces it too.
      if (v.scope === 'people' && !(v.people ?? []).length) {
        formCtx.setFieldError(
          'people',
          t('dashboard.views.peopleRequired', 'Choose at least one colleague'),
        )
        return
      }
      await onSubmit({
        name: v.name.trim(),
        scope: v.scope,
        people: v.scope === 'people' ? v.people ?? [] : [],
      })
    },
    [onSubmit, t],
  )

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {mode === 'create'
            ? t('dashboard.views.saveTitle', 'Save this view')
            : t('dashboard.views.editTitle', 'Rename or share view')}
        </DialogTitle>
        <DialogDescription>
          {mode === 'create'
            ? t(
                'dashboard.views.saveDescription',
                "Saves your cards, their order and width, and each card's filters and columns. Pick it from Views to come back to this dashboard.",
              )
            : t('dashboard.views.editDescription', 'Change the name, or who can open this view.')}
        </DialogDescription>
      </DialogHeader>
      <div className="py-2">
        <Form className="flex flex-col gap-4" />
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isPending}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button type="submit" form={FORM_ID} disabled={isPending}>
          {isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
          {mode === 'create'
            ? t('dashboard.views.saveButton', 'Save view')
            : t('dashboard.views.editButton', 'Save changes')}
        </Button>
      </DialogFooter>
    </>
  )
}
